import Header from "../../components/Header.jsx";
import {
    Button,
    Checkbox,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    useDisclosure
} from "@heroui/react";
import Languages from "../../components/textToSpeech/Languages.jsx";
import VoiceCloneVoiceCard from "../../widgets/VoiceCloneVoiceCard.jsx";
import AudioRecording from "../../widgets/AudioRecording.jsx";
import {useRef, useState} from "react";
import toast from 'react-hot-toast';
import AudioUpload from "../../widgets/AudioUploader.jsx";
import {useForm} from "@mantine/form";
import { isEqual } from 'lodash';
import {useMutation, useQueryClient} from "@tanstack/react-query";
import $http from "../../helpers/axios.jsx";
import {useNavigate} from "react-router-dom";
import {motion} from 'framer-motion';

const ALLOWED_SAMPLE_FILE = 10

/* Rainbow audio waveform — centered on a midline, gently rippling. Fixed-size
   so it never shifts layout. Mirrors the icon used in the app header. */
const WAVE_BARS = [
    { h: 18, c: '#8b2fc9' }, { h: 40, c: '#c026d3' }, { h: 64, c: '#ec4899' },
    { h: 50, c: '#ef4444' }, { h: 85, c: '#f97316' }, { h: 68, c: '#eab308' },
    { h: 100, c: '#84cc16' }, { h: 90, c: '#22c55e' }, { h: 72, c: '#10b981' },
    { h: 94, c: '#06b6d4' }, { h: 58, c: '#3b82f6' }, { h: 40, c: '#6366f1' },
    { h: 26, c: '#8b5cf6' }, { h: 16, c: '#a855f7' },
];
// Bright pastel palette for use on dark / gradient backgrounds (e.g. the modal
// header) where the dark rainbow above would blend in.
const WAVE_LIGHT = ['#ffffff', '#fbcfe8', '#fda4af', '#fecaca', '#fed7aa', '#fde68a', '#d9f99d', '#bbf7d0', '#a7f3d0', '#a5f3fc', '#bfdbfe', '#c7d2fe', '#ddd6fe', '#f5d0fe'];
function Waveform({ width = 46, height = 26, light = false }) {
    return (
        <span className="flex flex-none items-center justify-center gap-[1.5px]" style={{ width, height }} aria-hidden="true">
            {WAVE_BARS.map((b, i) => (
                <span key={i} className="block w-[2px] rounded-full"
                    style={{ height: `${b.h}%`, background: light ? WAVE_LIGHT[i] : b.c, transformOrigin: 'center', animation: `vcWave 1.2s ease-in-out ${i * 0.08}s infinite` }} />
            ))}
        </span>
    );
}

/* Numbered heading for each step card. */
function StepHead({ n, title, desc, done }) {
    return (
        <div className="mb-4 flex items-start gap-3">
            <span className={`grid h-8 w-8 flex-none place-items-center rounded-xl text-[13px] font-bold text-white shadow-sm transition ${done ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gradient-to-br from-primary to-fuchsia-500'}`}>
                {done ? <i className="fa-solid fa-check text-[12px]" /> : n}
            </span>
            <div className="min-w-0">
                <h3 className="text-[15px] font-semibold leading-tight text-slate-800">{title}</h3>
                {desc && <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">{desc}</p>}
            </div>
        </div>
    );
}

/* One labelled row in the confirm-modal recap. */
function InfoRow({ icon, label, value }) {
    return (
        <div className="flex items-center justify-between px-4 py-2.5">
            <span className="flex items-center gap-2 text-[12.5px] text-slate-500"><i className={`${icon} text-slate-400`} /> {label}</span>
            <span className="max-w-[55%] truncate text-right text-[13px] font-medium capitalize text-slate-800">{value}</span>
        </div>
    );
}

/* One row in the readiness checklist. */
function Check({ ok, children }) {
    return (
        <li className="flex items-center gap-2.5 text-[13px]">
            <span className={`grid h-5 w-5 flex-none place-items-center rounded-full transition ${ok ? 'bg-primary text-white' : 'bg-slate-100 text-slate-300'}`}>
                <i className={`fa-solid ${ok ? 'fa-check' : 'fa-minus'} text-[9px]`} />
            </span>
            <span className={ok ? 'font-medium text-slate-700' : 'text-slate-400'}>{children}</span>
        </li>
    );
}

const VoiceCloning = () =>{
    const {isOpen:resetModalIsOpen,  onOpenChange:resetModalOnOpenChange} = useDisclosure()
    const {isOpen:voiceCloneModalIsOpen,  onOpenChange:voiceCloneModalOnOpenChange} = useDisclosure()
    const queryClient = useQueryClient();
    const [isClickedForAudioRecording, setClickedForAudioRecording] = useState(false)
    const [currentPlayingId, setCurrentPlayingId] = useState(null);
    const audioRef = useRef(null);
    const validateMediaStream = useRef(null);
    const navigate = useNavigate();
    const audioRecordingRef = useRef(null);

    const initialValues = {
        voice_name: "",
        language: "",
        files: [
        ],
        need_noise_reduction: false,
        select_terms: false
    }
    const form = useForm({
        initialValues: initialValues
    })

    const clickAudioRecordingHandler = () => {
        setClickedForAudioRecording(false)
        const stream = validateMediaStream.current;
        if (stream) {
            stream.getAudioTracks().forEach((track) => track.stop());
        }
    }

    const recordingStartHandler = async () => {
        try {
            validateMediaStream.current = await navigator.mediaDevices.getUserMedia({audio: true});
            setClickedForAudioRecording(true)
            handlePlay(null);
        } catch (err) {
            toast.error('Recording permissions are not enabled in your browser. Please allow access.');
        }
    }

    const recordedDataHandler = (recording) => {
        form.setFieldValue('files' ,pre=> [...pre, recording])
    }

    const convertButtonActiveStatus = () =>{
        return form.values.files?.length > 0 && form.values?.voice_name && form.values?.language && form.values?.select_terms
    }

    const handlePlay = (recording) => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current = null;
            }

            if(!recording){
                setCurrentPlayingId(null);
                return;
            }

            if (currentPlayingId === recording.id) {
                setCurrentPlayingId(null);
                return;
            }

            audioRecordingRef.current?.cancelRecording()

            const filePath = URL.createObjectURL(recording.blob);
            const newAudio = new Audio(filePath);

            newAudio.onended = () => {
                setCurrentPlayingId(null);
                audioRef.current = null;
            };

            newAudio.play();
            audioRef.current = newAudio;
            setCurrentPlayingId(recording.id);
        }
        catch (err) {
            toast.error("No valid recording found. Please record again.");
            console.error(err);
        }

    };

    const removeAudioRecordingHandler = (recording) => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current = null;
            }
            setCurrentPlayingId(null);
            const filteredRecording = form.values.files?.filter(record => record.id !== recording?.id);
            form.setFieldValue('files' ,filteredRecording)
        }
        catch (error) {
            toast.error(error)
        }
    }

    const voiceCloneHandler = useMutation({
        mutationKey: ['voice-clone'],
        mutationFn: (payload) => {
            return $http.post(`/projects/voice-cloning`, payload, {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            }).then(res => res.data);
        },
        onSuccess: (response) => {
            queryClient.refetchQueries({
                queryKey: ['auth']
            })
            queryClient.refetchQueries({
                queryKey :  ['myVoiceList', 'own']
            })
            navigate("/voices?type=my_voice")
            toast.success("file uploaded successfully.");
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'Something went wrong');
        }
    });

    const voiceCloneSubmitHandler = () => {
        const formData = new FormData();
        formData.append("voice_name", form.values.voice_name);
        formData.append("language", form.values.language);
        formData.append("need_noise_reduction", form.values.need_noise_reduction);
        formData.append("select_terms", form.values.select_terms);
        form.values.files.forEach((file) => {
            formData.append("files", file.blob, file.name);
            formData.append("durations", file.time);
        });
        voiceCloneHandler.mutate(formData);
    };

    // Live readiness state — drives the checklist + progress in the sticky rail.
    const ready = {
        samples: form.values.files?.length > 0,
        name: !!form.values.voice_name,
        language: !!form.values.language,
        terms: !!form.values.select_terms,
    };
    const readyCount = Object.values(ready).filter(Boolean).length;
    const canConvert = convertButtonActiveStatus();
    const dirty = !isEqual(form.values, initialValues);

    const cardCls = "rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_10px_40px_-26px_rgba(15,23,42,0.3)]";

    return (
        <div className="relative">
            <Header currentPage="Voice Cloning"/>
            <style>{`@keyframes vcWave{0%,100%{transform:scaleY(.5)}50%{transform:scaleY(1)}}`}</style>

            <div className="mx-auto max-w-6xl px-4 mt-5 mb-24">
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 240, damping: 26 }}
                    className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-[#f6f2ff] via-white to-[#fdf2fb] px-6 py-7 sm:px-8">
                    <span aria-hidden className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-fuchsia-300/25 blur-3xl"/>
                    <span aria-hidden className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-primary/20 blur-3xl"/>
                    <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
                        <div className="grid h-16 w-16 flex-none place-items-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
                            <Waveform width={42} height={26}/>
                        </div>
                        <div className="flex-1">
                            <h1 className="text-[22px] font-semibold tracking-tight text-slate-900">Clone your voice</h1>
                            <p className="mt-1 max-w-xl text-[13.5px] leading-relaxed text-slate-600">
                                A clean clip as short as <strong className="font-semibold text-slate-700">10 seconds</strong> is enough.
                                We’ll train a custom voice you can reuse across every dub.
                            </p>
                        </div>
                        <div className="flex-none self-start rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-center sm:self-auto">
                            <div className="flex items-center justify-center gap-1.5 text-amber-700">
                                <i className="hgi-stroke hgi-coins-01 text-lg"/>
                                <span className="text-[18px] font-bold leading-none tabular-nums">12,000</span>
                            </div>
                            <p className="mt-1 text-[11px] font-medium text-amber-600">credits · one-time</p>
                        </div>
                    </div>
                </motion.div>

                {/* Two-column workspace */}
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* ---- main column: the steps ---- */}
                    <div className="flex flex-col gap-5 lg:col-span-2">

                        {/* Step 1 — import the voice */}
                        <div className={cardCls}>
                            <StepHead n={1} done={ready.samples} title="Import the voice"
                                desc="Upload up to 10 clips, or record 10–60 seconds right here."/>

                            <div className="flex flex-row gap-4">
                                <AudioUpload isAudioRecordingStarted={isClickedForAudioRecording} recordingData={form.values.files} recordedDataHandler={recordedDataHandler} allowedSampleFile={ALLOWED_SAMPLE_FILE}/>

                                <div className="flex flex-col items-center justify-center">
                                    <div className="w-px h-20 bg-slate-200" />
                                    <span className="py-2 text-slate-400 text-xs">or</span>
                                    <div className="w-px h-20 bg-slate-200" />
                                </div>

                                <div className={`flex-1 relative flex flex-col items-center justify-center rounded-xl border border-slate-300 bg-[#fcfcfd] p-6 transition ${form.values.files?.length < 10 ? 'hover:border-[#8a4af3] cursor-pointer' : 'cursor-auto'}`}>
                                    {!isClickedForAudioRecording ? (
                                        <div onClick={recordingStartHandler} className="flex h-full w-full flex-col items-center justify-center">
                                            <span className="mb-2.5 grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
                                                <i className="fa-solid fa-microphone text-[18px]" />
                                            </span>
                                            <p className="text-sm font-medium text-slate-700">Record audio</p>
                                            <p className="pt-0.5 text-[10px] text-slate-400">10–60 seconds long</p>
                                        </div>
                                    ) : (
                                        <AudioRecording
                                            ref={audioRecordingRef}
                                            clickAudioRecordingHandler={clickAudioRecordingHandler}
                                            isClickedForAudioRecording={isClickedForAudioRecording}
                                            recordedDataHandler={recordedDataHandler}
                                            removeAudioRecordingHandler={removeAudioRecordingHandler}
                                            validateMediaStream={validateMediaStream}
                                        />
                                    )}
                                    {form.values.files?.length >= ALLOWED_SAMPLE_FILE && (
                                        <div className="absolute left-0 top-0 z-50 h-full w-full rounded-xl border-slate-300 bg-slate-50 opacity-60"></div>
                                    )}
                                </div>
                            </div>

                            {/* uploaded samples */}
                            <div className="mt-5">
                                <div className="mb-2 flex items-center justify-between">
                                    <p className="text-[13px] font-medium text-slate-700">Uploaded samples</p>
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${form.values.files?.length > 0 ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'}`}>
                                        {form.values.files?.length}/{ALLOWED_SAMPLE_FILE}
                                    </span>
                                </div>

                                {form.values.files?.length === 0 && (
                                    <div className="flex flex-row gap-2.5 rounded-xl bg-slate-50 p-4">
                                        <i className="fa-solid fa-circle-info mt-[2px] text-slate-400"></i>
                                        <div className="flex flex-col gap-2">
                                            <p className="m-0 p-0 text-[12px] leading-5 text-slate-500">Upload a clear recording from a quiet room. Background noise, echo, or multiple voices will reduce clone quality.</p>
                                            <p className="m-0 p-0 text-[12px] leading-5 text-slate-500">You can use noise removal below, but it may soften detail — pick the cleanest segments when you can.</p>
                                            <p className="m-0 p-0 text-[12px] leading-5 text-slate-500">For best results, submit <strong className="font-semibold text-slate-600">5–10</strong> clear samples.</p>
                                        </div>
                                    </div>
                                )}

                                {form.values.files?.length > 0 && (
                                    <div className="flex flex-col justify-center gap-2 rounded-xl border border-slate-200/70 bg-slate-50/60 p-3">
                                        {form.values.files?.map((recording) => (
                                            <VoiceCloneVoiceCard
                                                key={recording.id}
                                                recording={recording}
                                                isPlaying={currentPlayingId === recording.id}
                                                removeAudioRecordingHandler={removeAudioRecordingHandler}
                                                onPlay={() => handlePlay(recording)}/>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* noise reduction */}
                            <label className="mt-4 flex cursor-pointer items-center gap-2">
                                <Checkbox
                                    isSelected={form.values.need_noise_reduction}
                                    onValueChange={(value) => form.setFieldValue('need_noise_reduction', value)}
                                    size="lg" color="default" className="m-0" classNames={{ wrapper: 'me-0' }}></Checkbox>
                                <span className="text-[12px] font-light leading-[20px] text-slate-600">Remove background noise from audio samples (optional — may reduce sound detail).</span>
                            </label>
                        </div>

                        {/* Step 2 — name */}
                        <div className={cardCls}>
                            <StepHead n={2} done={ready.name} title="Name your voice"
                                desc="Pick a memorable name — you’ll see it in your voice list."/>
                            <div className="relative">
                                <Input
                                    type="text"
                                    value={form.values.voice_name}
                                    onChange={(e) => {
                                        if( e.target.value.length <= 30 ) {
                                            form.setValues(prevState => ({
                                                ...prevState,
                                                voice_name: e.target.value
                                            }));
                                        }
                                    }}
                                    classNames={{
                                        inputWrapper: "!shadow-none h-[42px] px-4 w-full bg-[#fcfcfd] focus-within:!border-slate-800 focus-within:!bg-[#fcfcfd] hover:!bg-transparent rounded-xl border border-[#12131714] focus-within:border-slate-800",
                                        input: "!text-sm !font-light text-gray-400 placeholder:text-gray-400 outline-none",
                                    }}
                                    placeholder="Name your clone voice"/>
                                <p className="absolute right-2 top-[40%] -translate-y-1.5 text-sm font-light text-gray-400">{form.values.voice_name?.length} / 30</p>
                            </div>
                        </div>

                        {/* Step 3 — language */}
                        <div className={cardCls}>
                            <StepHead n={3} done={ready.language} title="Language"
                                desc="For best results, match the language of the original audio."/>
                            <Languages classNames={{
                                trigger : "h-[42px] focus:border-slate-800 px-4 w-full bg-[#fcfcfd] rounded-xl text-sm border-1 border-[#12131714] outline-0",
                                value : "text-gray-400 !font-light",
                                listbox : "text-gray-700 !font-md",
                                listboxWrapper : "!p-0",
                            }} placeholder="Language"
                               onChanged={(value) => {
                                   form.setFieldValue('language', value)
                               }}
                               form={form}
                            />
                        </div>

                        {/* consent */}
                        <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl border border-slate-200/80 bg-white p-4">
                            <Checkbox
                                size="lg"
                                isSelected={form.values.select_terms}
                                onValueChange={(value) => form.setFieldValue('select_terms', value)}
                                color="default" className="mt-0"/>
                            <span className="mt-[2px] p-0 text-[12px] font-light leading-[20px] text-slate-600">
                                I confirm that I possess all necessary rights and authorization to upload these voice samples for the purpose of generating AI content. I also reaffirm my commitment to comply with the
                                <a className="cursor-pointer underline underline-offset-2 opacity-80 hover:text-blue-800"> Terms of Service</a> and agree not to use any generated content for illegal or harmful activities.
                            </span>
                        </label>
                    </div>

                    {/* ---- sticky rail: readiness + actions + tips ---- */}
                    <aside className="lg:col-span-1">
                        <div className="flex flex-col gap-4 lg:sticky lg:top-24">
                            <div className={cardCls}>
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[14px] font-semibold text-slate-800">Ready to clone?</h3>
                                    <span className="text-[12px] font-semibold text-primary tabular-nums">{readyCount}/4</span>
                                </div>
                                <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                                    <div className="h-full rounded-full bg-gradient-to-r from-primary to-fuchsia-500 transition-[width] duration-500"
                                        style={{ width: `${(readyCount / 4) * 100}%` }} />
                                </div>

                                <ul className="mt-4 space-y-2.5">
                                    <Check ok={ready.samples}>Add at least one sample</Check>
                                    <Check ok={ready.name}>Name your voice</Check>
                                    <Check ok={ready.language}>Choose a language</Check>
                                    <Check ok={ready.terms}>Accept the terms</Check>
                                </ul>

                                <div className="mt-5 flex flex-col gap-2.5">
                                    <Button
                                        isLoading={voiceCloneHandler.isPending}
                                        onPress={()=>{
                                            if(!voiceCloneHandler.isPending && canConvert) {
                                                voiceCloneModalOnOpenChange();
                                            }
                                        }}
                                        className={`h-11 w-full rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 text-[14px] font-semibold text-white shadow-sm transition ${!canConvert ? 'cursor-not-allowed opacity-40' : 'hover:shadow-md'}`}>
                                        <i className="hgi hgi-stroke hgi-stars text-[16px]"></i> Convert voice
                                    </Button>
                                    <Button
                                        disabled={voiceCloneHandler.isPending || !dirty}
                                        onPress={()=>{
                                            if(!voiceCloneHandler.isPending && dirty)
                                                resetModalOnOpenChange()
                                        }}
                                        className={`h-10 w-full rounded-xl bg-slate-100 text-[13px] text-slate-600 transition hover:bg-slate-200 ${(!dirty || voiceCloneHandler.isPending) ? 'cursor-not-allowed opacity-40 hover:bg-slate-100' : ''}`}>
                                        Reset
                                    </Button>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-5">
                                <p className="flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                                    <i className="fa-solid fa-lightbulb text-amber-400"></i> Tips for a great clone
                                </p>
                                <ul className="mt-3 space-y-2 text-[12px] leading-relaxed text-slate-500">
                                    <li className="flex gap-2"><i className="fa-solid fa-check mt-[3px] text-[10px] text-emerald-500"></i> Record in a quiet room, no echo.</li>
                                    <li className="flex gap-2"><i className="fa-solid fa-check mt-[3px] text-[10px] text-emerald-500"></i> One speaker only, natural pace.</li>
                                    <li className="flex gap-2"><i className="fa-solid fa-check mt-[3px] text-[10px] text-emerald-500"></i> 5–10 clean samples beat one long take.</li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <Modal isOpen={resetModalIsOpen} onOpenChange={resetModalOnOpenChange}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>
                                <p>Are you sure?</p>
                            </ModalHeader>
                            <ModalBody>
                                <p>If you reset, all your contents will be discarded</p>
                            </ModalBody>
                            <ModalFooter>
                                <Button className="bg-[#c8ceda4d]" onPress={resetModalOnOpenChange}>Cancel</Button>
                                <Button className="bg-[#7659fa] text-white" onPress={()=>{
                                    form.reset();
                                    handlePlay(null);
                                    resetModalOnOpenChange();
                                }}>Confirm</Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            <Modal isOpen={voiceCloneModalIsOpen} onOpenChange={voiceCloneModalOnOpenChange} hideCloseButton size="md" classNames={{ base: 'rounded-3xl overflow-hidden' }}>
                <ModalContent>
                    {(onClose) => (
                        <div>
                            {/* gradient header with the animated waveform */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-primary to-fuchsia-600 px-6 pb-7 pt-6 text-white">
                                <span aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                                <span aria-hidden className="pointer-events-none absolute -bottom-12 left-10 h-32 w-32 rounded-full bg-fuchsia-300/30 blur-2xl" />
                                <div className="relative flex items-center gap-3.5">
                                    <div className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-white/15 ring-1 ring-white/30 backdrop-blur">
                                        <Waveform width={34} height={22} light />
                                    </div>
                                    <div>
                                        <h3 className="text-[17px] font-semibold leading-tight">Confirm voice cloning</h3>
                                        <p className="mt-0.5 text-[12.5px] text-white/80">One-time training of your custom voice</p>
                                    </div>
                                </div>
                            </div>

                            {/* recap + cost */}
                            <div className="px-6 py-5">
                                <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200">
                                    <InfoRow icon="fa-solid fa-tag" label="Voice name" value={form.values.voice_name || '—'} />
                                    <InfoRow icon="fa-solid fa-file-audio" label="Samples" value={`${form.values.files?.length || 0} clip${form.values.files?.length === 1 ? '' : 's'}`} />
                                    <InfoRow icon="fa-solid fa-globe" label="Language" value={form.values.language || '—'} />
                                </div>

                                <div className="mt-4 flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                                    <span className="flex items-center gap-2 text-[13px] font-medium text-amber-700">
                                        <i className="hgi-stroke hgi-coins-01 text-lg" /> One-time cost
                                    </span>
                                    <div className="text-right">
                                        <div className="text-[18px] font-bold leading-none tabular-nums text-amber-700">12,000</div>
                                        <div className="text-[11px] text-amber-600">credits</div>
                                    </div>
                                </div>
                            </div>

                            {/* actions */}
                            <div className="flex gap-3 px-6 pb-6">
                                <Button onPress={onClose}
                                    className="h-11 flex-1 rounded-xl bg-slate-100 text-[14px] font-medium text-slate-700 transition hover:bg-slate-200">
                                    Cancel
                                </Button>
                                <Button isLoading={voiceCloneHandler.isPending}
                                    onPress={()=>{
                                        voiceCloneSubmitHandler();
                                        voiceCloneModalOnOpenChange();
                                    }}
                                    className="h-11 flex-1 rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 text-[14px] font-semibold text-white shadow-sm transition hover:shadow-md">
                                    <i className="hgi hgi-stroke hgi-stars text-[16px]" /> Confirm &amp; clone
                                </Button>
                            </div>
                        </div>
                    )}
                </ModalContent>
            </Modal>

        </div>
    )
}

export default VoiceCloning;
